import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import React, { useRef, useState, useEffect } from "react";
import { User, getAuth, Unsubscribe, onAuthStateChanged, signInAnonymously } from "firebase/auth";
import { LoadAsset } from "app/LoadAsset";
import { useFetchUserData } from "app/hook";
import { AppDispatch, RootState } from "app/store";
import { DialogType, ParentClaimStatus } from "app/types";
import { actions as configActions } from "app/configSlice";
import { isDev } from "common/utils";
import parentI18n from "features/parent/i18n";
import { actions } from "features/user/slice";
import { connectEmulator } from "./api";
import { parentsFirebase } from "./Auth";

export const AuthProvider = (props: { children: React.ReactNode | null }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const parentRegistrationVisible = useSelector(
    (state: RootState) => state.config.dialog.parent_registration.value,
  );
  const parentRegistrationVisibleRef = useRef(parentRegistrationVisible);
  useEffect(() => {
    parentRegistrationVisibleRef.current = parentRegistrationVisible;
  }, [parentRegistrationVisible]);
  const parentRegistrationArgs = useSelector(
    (state: RootState) => state.config.dialog.parent_registration.args,
  );
  const parentRegistrationArgsRef = useRef(parentRegistrationArgs);
  useEffect(() => {
    parentRegistrationArgsRef.current = parentRegistrationArgs;
  }, [parentRegistrationArgs]);
  const lastClaimStatus = useSelector((state: RootState) => state.config.lastClaimStatus);
  const lastClaimStatusRef = useRef(lastClaimStatus);
  useEffect(() => {
    lastClaimStatusRef.current = lastClaimStatus;
  }, [lastClaimStatus]);
  const parentsAuth = getAuth(parentsFirebase);
  const [loading, setLoading] = useState(true);
  const [latestVersion, setLatestVersion] = useState(false);
  const { handleFetchParentsUser, handleFetchChildrenUser } = useFetchUserData();
  const [isChildrenAuthenticating, setIsChildrenAuthenticating] = useState(true);
  const [isParentsAuthenticating, setIsParentsAuthenticating] = useState(true);

  useEffect(() => {
    if (isDev) {
      const auth = getAuth();
      connectEmulator(auth, parentsAuth);
    }
  }, []);

  useEffect(() => {
    let parentsUnsubscribe: Unsubscribe;
    if (latestVersion) {
      parentsUnsubscribe = onAuthStateChanged(
        parentsAuth,
        async (user: User) => {
          if (user && !user?.isAnonymous) {
            try {
              dispatch(configActions.setLoading(true));
              handleFetchParentsUser({
                uid: user.uid,
                callback: (redirectTo: string) => {
                  const wasInAppRegistration = parentRegistrationVisibleRef.current;
                  if (wasInAppRegistration) {
                    // Modal を閉じる/遷移する唯一の責務はここ（データ取得完了後）
                    dispatch(
                      configActions.updateDialog({
                        type: DialogType.PARENT_REGISTRATION,
                        value: false,
                      }),
                    );
                    if (lastClaimStatusRef.current !== ParentClaimStatus.CLAIMED) {
                      toast(
                        <div>
                          <p className="type-text type-body text-textcolor-black">
                            {parentI18n.t("MSG_TRIAL_DATA_NOT_CLAIMED")}
                          </p>
                        </div>,
                      );
                      dispatch(actions.clearActiveUser());
                      navigate("/", { replace: true });
                    } else {
                      const target =
                        parentRegistrationArgsRef.current?.redirectAfterSuccess ?? "/parent";
                      navigate(target, { replace: true });
                    }
                  } else if (redirectTo) {
                    navigate(redirectTo, { replace: true });
                  }
                  dispatch(configActions.setLoading(false));
                  setIsParentsAuthenticating(false);
                },
              });
            } catch (error) {
              dispatch(configActions.setLoading(false));
              console.error(error);
            }
          } else {
            if (!user) {
              await signInAnonymously(parentsAuth);
            }
            dispatch(actions.clearUser());
            setIsParentsAuthenticating(false);
          }
        },
        (error) => console.error(JSON.stringify(error)),
      );
    }

    return () => {
      if (parentsUnsubscribe) {
        parentsUnsubscribe();
      }
    };
  }, [latestVersion]);

  useEffect(() => {
    let childrenUnsubscribe: Unsubscribe;
    if (latestVersion && !isParentsAuthenticating) {
      const auth = getAuth();
      childrenUnsubscribe = onAuthStateChanged(
        auth,
        async (user: User) => {
          if (user && (!parentsAuth.currentUser || parentsAuth.currentUser?.isAnonymous)) {
            dispatch(configActions.setLoading(true));
            handleFetchChildrenUser({
              uid: user.uid,
              callback: () => {
                dispatch(configActions.setLoading(false));
                setIsChildrenAuthenticating(false);
              },
            });
          } else {
            if (auth.currentUser) {
              await auth.signOut();
            }
            setIsChildrenAuthenticating(false);
          }
        },
        (error) => console.error(JSON.stringify(error)),
      );
    }

    return () => {
      if (childrenUnsubscribe) {
        childrenUnsubscribe();
      }
    };
  }, [latestVersion, isParentsAuthenticating]);

  return (
    <>
      <LoadAsset
        isAuthenticating={isChildrenAuthenticating || isParentsAuthenticating}
        loading={loading}
        setLoading={setLoading}
        setLatestVersion={setLatestVersion}
      />
      {loading || isChildrenAuthenticating || isParentsAuthenticating ? <></> : props.children}
    </>
  );
};
